<script setup>
import Vue, {computed, inject, reactive, ref} from "vue";
import ConfirmModal from "@/components/ui/ConfirmModal.vue";
import {getNoun, getNumberWithNoun} from "@/helpers/string";
import needsItemService from "@/services/needs-item.service";
import {useStore} from "vue2-helpers/vuex";
import {statuses} from "@/constants/needs";
import Button from "@/components/ui/Button.vue";
import Modal from "@/components/ui/Modal.vue";
import {triggerUpsell} from "@/composables/useStoreActions";

const store = useStore();

const need = inject('need');
const solutions = inject('solutions');
const getNeedInfo = inject('getNeedInfo');

const steps = [
    'Add suggested solutions',
    'Rate all your solutions',
    'Escalate need to innovation team'
]

const showConfirmEscalateModal = ref(false);
const showConfirmConvertModal = ref(false);
const canForceEscalate = ref(false);
const showModal = ref(false);
const showSuccessModal = ref(false);
const loading = ref(false);

const form = reactive({
    comment: ''
});

const solutionsCount = computed(() => solutions.value.length);
const aiSolutionsCount = computed(() => solutions.value.filter(item => item.source === 'ai').length);
const ecoSolutionsCount = computed(() => solutions.value.filter(item => item.source === 'ecosystem').length);
const reviewedSolutionsCount = computed(() => solutions.value.filter(startup => startup.rating > 0).length);
const unreviewedSolutionsCount = computed(() => solutions.value.filter(startup => !startup.rating).length);

function requestEscalate() {
    if (!solutionsCount.value) return;

    if (solutionsCount.value === reviewedSolutionsCount.value || canForceEscalate.value) {
        showModal.value = true;
    } else {
        showConfirmEscalateModal.value = true;
    }
}

function escalationConfirmed(option) {
    showConfirmEscalateModal.value = false;

    if (option === 'escalate') {
        canForceEscalate.value = true;
        requestEscalate();
    }
}

async function escalate() {
    loading.value = true;
    const {data} = await needsItemService.escalate(need.value.id, form);

    if (data) {
        showModal.value = false;
        showSuccessModal.value = true;

        getNeedInfo();
    }

    loading.value = false;
}

async function setStatus(status) {
    need.value.status = status;
    await needsItemService.setStatus(need.value.id, status);
}

function requestConvert() {
    if (store.getters.onlyNeeds && need.value.converted_count >= 3) {
        triggerUpsell(store);
    } else {
        showConfirmConvertModal.value = true;
    }
}

function conversionConfirmed(option) {
    showConfirmConvertModal.value = false;

    if (option === 'convert') {
        convert();
    }
}

async function convert() {
    const {data} = await needsItemService.convert(need.value.id);

    if (data) {
        Vue.notify({
            group: 'app',
            type: 'success',
            duration: 5000,
            text: `Tracker created <a class="btn btn-outline-success btn-notify btn-lg ml-2" href="/trackers/${data}" target="_blank">Open tracker</a>`
        });

        getNeedInfo();
    }
}
</script>

<template>
    <div>
        <ul v-if="!need.escalated || !store.getters.isAdmin" class="list-plan">
            <li
                v-for="(step, index) in steps"
                :key="index"
                :class="{'list-plan__done': need.work_status > index}"
            >
                {{step}}
            </li>
        </ul>

        <div v-if="need.escalated && store.getters.isAdmin">
            <div class="mb-3">
                Status
            </div>

            <Dropdown
                :text="need.status ? statuses[need.status] : statuses['unreviewed']"
                class="dropdown-status"
                :disabled="need.status === 'converted'"
            >
                <template v-for="(label, status) in statuses">
                    <DropdownItem
                        v-if="status !=='converted' || need.status === 'converted'"
                        :key="status"
                        @click="setStatus(status)"
                    >
                        {{label}}
                    </DropdownItem>
                </template>
            </Dropdown>
        </div>

        <div v-if="need.escalated && need.status !== 'converted' && !need.tracker_id" class="card-needs__sidebar mt-3">
            <p>Ready to start performing diligence?</p>

            <button class="button-tracker" @click="requestConvert">Convert to Tracker</button>

            <ConfirmModal
                :show.sync="showConfirmConvertModal"
                title="Convert To Tracker"
                body="Converting this need to a tracker will move the data to a tracker and this need will be archived."
                :options="[
                {label: 'Cancel', value: 'cancel', class: 'btn-white'},
                {label: 'Convert', value: 'convert'}
            ]"
                modal-class="modal-escalateConfirm"
                @clicked="conversionConfirmed"
            />
        </div>

        <template v-if="!need.escalated">
            <a @click="requestEscalate" class="btn-xl btn-xl-export mb-3" :class="{ disabled: !solutionsCount }">Escalate Need</a>

            <p class="mb-0"><b>Escalate</b> to send this need and your assessed solutions to your innovation team for further review</p>

            <ConfirmModal
                :show.sync="showConfirmEscalateModal"
                title="Unreviewed Solutions"
                :body="`There ${getNoun('is', unreviewedSolutionsCount, 'are')} ${getNumberWithNoun('solution', unreviewedSolutionsCount)} that you have not reviewed. Are you sure you want to escalate this need?`"
                :options="[
                {label: 'Cancel', value: 'cancel', class: 'btn-white'},
                {label: 'Escalate', value: 'escalate'}
            ]"
                modal-class="modal-escalateConfirm"
                @clicked="escalationConfirmed"
            />

            <Modal v-model="showModal" title="Escalate Need" modal-class="modal-escalate">
                <p>
                    Escalating this need will send this project along with your solutions and reviews to your innovation team. You will no longer be able to make any changes to it.
                </p>

                <div class="modal-escalate__inner">
                    <h5 class="heading5 heading5--secondary">{{need.name}}</h5>

                    <p>{{need.description}}</p>

                    <ul class="escalate-stats">
                        <li class="escalate-stats__startups">{{getNumberWithNoun('startup', aiSolutionsCount) }} </li>
                        <li class="escalate-stats__ecosystem">{{getNumberWithNoun('startup', ecoSolutionsCount) }} recommended</li>
                        <li class="escalate-stats__review">{{getNumberWithNoun('startup', reviewedSolutionsCount) }} reviewed</li>
                    </ul>
                </div>

                <form class="form">
                    <div class="form__item">
                        <textarea
                            v-model="form.comment"
                            class="form-control form-control--mid mb-3"
                            placeholder="Add a note (optional)"
                        />
                    </div>
                </form>

                <template #modal-footer>
                    <Button
                        @click="escalate"
                        class="btn-xl btn-xl-export"
                    >
                        Escalate Need
                    </Button>
                </template>
            </Modal>
        </template>

        <Modal v-model="showSuccessModal" title="Need Escalated Successfully" hide-footer modal-class="modal-primary modal-need-success">
            <div>
                <p>Your need, solutions and ratings were sent to your innovation team.</p>

                <div class="modal-need-success-center">
                    <button @click="showSuccessModal = false" class="btn btn-xl btn-xl-basic">Close</button>
                </div>
            </div>
        </Modal>
    </div>
</template>

<style scoped>
.btn-xl.disabled {
    opacity: 0.5;
    pointer-events: none;
}
</style>
